import { useState } from "react";
import { Download, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

type Status = "idle" | "loading" | "success" | "error";

type Props = {
  /** Endpoint that streams the PDF (or other file) */
  href: string;
  /** Suggested filename for the saved file */
  filename: string;
  /** Visual variant — `icon` is square, `full` shows label */
  variant?: "icon" | "full";
  /** Optional aria/title text override */
  label?: string;
  /** Optional extra class names */
  className?: string;
};

/**
 * Stateful download button: fetches the file as a blob, surfaces a clear
 * loading spinner, transient success tick, and an error state with toast.
 * Falls back to a regular anchor download if the Fetch fails for any reason
 * (e.g. CORS, offline) so the user can still try the underlying URL.
 */
export function QuickDownloadButton({
  href,
  filename,
  variant = "icon",
  label = "Quick download",
  className,
}: Props) {
  const [status, setStatus] = useState<Status>("idle");

  async function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch(href, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStatus("success");
      toast.success("Download started", { description: filename });
      setTimeout(() => setStatus("idle"), 1600);
    } catch (err: any) {
      setStatus("error");
      toast.error("Download failed", {
        description: err?.message ?? "Please try again or check your connection.",
        action: {
          label: "Open link",
          onClick: () => window.open(href, "_blank", "noopener"),
        },
      });
      setTimeout(() => setStatus("idle"), 2400);
    }
  }

  const isLoading = status === "loading";
  const isSuccess = status === "success";
  const isError = status === "error";

  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition disabled:cursor-not-allowed";
  const colors = isError
    ? "border-rose-300 bg-rose-50 text-rose-700"
    : isSuccess
      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
      : "border-border bg-background text-navy hover:border-navy/40 hover:bg-secondary";
  const sizing = variant === "icon" ? "px-3 py-2" : "px-3 py-2";

  const icon = isLoading ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin" />
  ) : isSuccess ? (
    <Check className="h-3.5 w-3.5" />
  ) : isError ? (
    <AlertCircle className="h-3.5 w-3.5" />
  ) : (
    <Download className="h-3.5 w-3.5" />
  );

  const text = isLoading
    ? "Preparing…"
    : isSuccess
      ? "Downloaded"
      : isError
        ? "Retry"
        : label;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      aria-busy={isLoading}
      aria-label={label}
      title={label}
      className={[base, colors, sizing, className ?? ""].join(" ")}
    >
      {icon}
      {variant === "full" && <span>{text}</span>}
    </button>
  );
}
